/**
 * SchoolGuard — wrapper que mostra loading/404 enquanto school resolve.
 *
 * Comportamento:
 *   - isMainSite OU school resolvido OK → renderiza children normalmente
 *   - schoolLoading → tela de loading com spinner + slug
 *   - notFound → tela 404 amigavel ("essa escola nao existe")
 *
 * Uso: envolver rotas /aluno e /admin pra que tenants invalidos vejam UX bonita.
 */

import { useSchool } from "@/contexts/SchoolContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { School2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import type { ReactNode } from "react";

export function SchoolGuard({ children }: { children: ReactNode }) {
  const { isLoading, notFound, slug, isMainSite, school } = useSchool();

  // Main site (whitelabel) — sem tenant context, deixa passar
  if (isMainSite) return <>{children}</>;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div className="text-center space-y-1">
          <p className="text-sm font-medium">Carregando escola…</p>
          {slug && <p className="text-xs text-muted-foreground font-mono">{slug}.monetizai.online</p>}
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center space-y-5">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700 mx-auto">
              <AlertCircle className="h-7 w-7" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">Escola não encontrada</h1>
              <p className="text-sm text-muted-foreground">
                Não existe nenhuma escola cadastrada no subdomínio{" "}
                <code className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono">
                  {slug ?? "?"}.monetizai.online
                </code>
              </p>
            </div>
            <div className="space-y-3 pt-2">
              <p className="text-xs text-muted-foreground">
                É a sua escola? Acesse o painel pra criar ou conferir o slug.
              </p>
              <a href="https://monetizai.online/admin/schools">
                <Button className="w-full gap-2">
                  Ir pro painel <ArrowRight className="h-4 w-4" />
                </Button>
              </a>
              <a href="https://monetizai.online">
                <Button variant="outline" className="w-full gap-2">
                  <School2 className="h-4 w-4" />
                  Conheça o MonetizAI
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // School resolvido OK
  return <>{children}</>;
}
